import { useCallback } from 'react';
import useSWR from 'swr';
import { z } from 'zod';

import { carsComplianceDocumentsList } from '~/data/generated/endpoints/cars/cars';
import { parseApiResponse } from '~/data/parseApiResponse';
import { carCertificateSchema } from '~/data/schemas/car';

// The `compliance_documents` action is a bare array, not a paginated envelope:
// it hand-builds a list and returns `Response(documents)` (`views/api/cars.py:768`)
// without ever calling `paginate_queryset`, and `CarsViewSet` declares
// `schema = BareListAutoSchema()` (`cars.py:663`) so the generated schema agrees.
// No `Array.isArray` normalisation is needed here — unlike the attachment list
// endpoints, this one can never emit an envelope.
const carComplianceDocumentsSchema = z.array(carCertificateSchema);

const certificatesFetcher = async (carId: string) => {
  const { data } = await carsComplianceDocumentsList(carId);
  return parseApiResponse(carComplianceDocumentsSchema, data, 'CarComplianceDocuments');
};

export const useCarCertificates = (carId: string | undefined) => {
  const { data, error, isLoading, mutate } = useSWR(
    carId ? ['certificate', carId] : null,
    () => (carId ? certificatesFetcher(carId) : null),
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
    },
  );

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return {
    certificates: data ?? [],
    isLoading,
    error,
    refresh,
  };
};
